import { useCallback } from "react";
import { toast } from "sonner";
import { useCreateOfferLine, useOfferLines, type OfferLineTemplate } from "@/hooks/use-offers";
import { nextSortOrderFor, templateLineFields } from "@/lib/offer-edit-calc";
import { ideaLineComment } from "@/lib/tilbudsarbeidsrom/idea-board";
import type { DBOfferIdeaCard } from "@/lib/db-types";

/**
 * «Lag budsjettlinje» på et Idé-kort (VIB-2980). Linjen lages som et vanlig
 * katalogvalg i hovedbudsjettet, med katalogproduktets navn og sats, kortets
 * tekst som kommentar og `source_idea_card_id` som gir «Fra idé».
 */
export function useCreateLineFromIdea(offerId: string, hourlyRate: number) {
  const { data: lines = [] } = useOfferLines(offerId);
  const createLine = useCreateOfferLine();

  const createFromCard = useCallback(
    async (card: DBOfferIdeaCard, template: OfferLineTemplate, qty: number | null) => {
      try {
        await createLine.mutateAsync({
          offer_id: offerId,
          ...templateLineFields(template, qty, hourlyRate),
          comment: ideaLineComment(card),
          sort_order: nextSortOrderFor(lines, template.phase, null),
          section_id: null,
          source_idea_card_id: card.id,
        });
        toast.success(`Budsjettlinje «${template.name}» lagt til`);
      } catch (err) {
        toast.error(`Kunne ikke lage budsjettlinjen: ${err instanceof Error ? err.message : "ukjent feil"}`);
      }
    },
    [offerId, hourlyRate, lines, createLine],
  );

  return { lines, createFromCard };
}
